import { DataService } from "./DataService";
import { UserTeamSettingsEntity, IUserTeamSettingsEntity } from "sh-models";
import { UserTeamSettingsDataProvider } from "../dataproviders/UserTeamSettingsDataProvider";
import IUserTeamSettingsDataService from "./IUserTeamSettingsDataService";

/**
 * UserTeamSettings Data Service
 *
 * Manages user-specific configuration per team (one entity per user per team).
 * Current properties: mutedScheduleGroupIds (selective notification control).
 * Extends DataService base class for three-tier caching (Memory → IndexedDB → Network).
 * User context obtained from auth token (no userId in URL).
 */
class UserTeamSettingsDataService extends DataService implements IUserTeamSettingsDataService {
    /**
     * Get UserTeamSettings for authenticated user in team.
     * Returns null if doesn't exist (lazy creation - only created on first write).
     * Base class handles three-tier caching (Memory → IndexedDB → Network).
     */
    public async getUserTeamSettings(teamId: string): Promise<UserTeamSettingsEntity | null> {
        const dataProvider = new UserTeamSettingsDataProvider(this.teamDatabase, teamId);
        return await this.getData(dataProvider);
    }

    /**
     * Save UserTeamSettings for authenticated user. Returns entity with new eTag.
     * Requires eTag for optimistic concurrency control (409 on mismatch).
     * Base class handles write flow: memory → IndexedDB → network.
     */
    public async saveUserTeamSettings(teamId: string, userTeamSettings: IUserTeamSettingsEntity): Promise<UserTeamSettingsEntity> {
        const dataProvider = new UserTeamSettingsDataProvider(this.teamDatabase, teamId);
        return await this.updateData(userTeamSettings, dataProvider);
    }

    /**
     * Clear UserTeamSettings from IndexedDB cache for a team.
     * Used to force network fetch on next load (e.g., after 409 eTag conflict).
     */
    public async clearCache(teamId: string): Promise<void> {
        if (this.teamDatabase) {
            await this.teamDatabase.deleteUserTeamSettings(teamId);
        }
    }

    /**
     * Reset sync state (no-op for UserTeamSettings).
     */
    public async resetSyncState(): Promise<void> {
        // UserTeamSettings doesn't track time-based sync state
    }
}

/**
 * Singleton instance exported for use by orchestrators.
 */
const service: IUserTeamSettingsDataService = new UserTeamSettingsDataService();
export default service;
